import { useState, useEffect, useCallback, useRef, useMemo } from 'preact/hooks';
import CopyToClipboard from 'react-copy-to-clipboard';
import { charityIsTiltify, charityIsTwitch } from '@util/common.js';
import TiltifySetup from '../social/tiltify/tiltify-setup.jsx';
import TwitchSetup from '../social/twitch/twitch-setup.jsx';
import PropTypes from 'prop-types';
import { CheckCircle, InfoCircle } from 'flowbite-react-icons/solid';
import toast from 'react-hot-toast';
import useTwitchConfig from '@hooks/useTwitchConfig.js';
import useTiltifyConfig from '@hooks/useTiltifyConfig.js';
import OBSDimensions from '@components/overlays/shared/obs-dimensions.jsx';
import TiltifyCampaignSelector from '@components/overlays/social/tiltify/tiltify-campaign-selector.jsx';
import LinkSetupStep from '@components/overlays/social/link-setup/link-setup-step.jsx';
import TiltifyPollSelector from '@components/overlays/social/tiltify/tiltify-poll-selector.jsx';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { tiltify } from '@playlive/tiltify-lib';
import { CHARITY, isTestMode } from '@util/const.js';
import { fakeTiltifyPoll } from '@data/example/index.js';
import TwitchCampaignViewer from '@components/overlays/social/twitch/twitch-campaign-viewer.jsx';

LinkSetup.propTypes = {
  charityType: PropTypes.string,
  showTiltifyPollSelector: PropTypes.bool,
  overlayURL: PropTypes.string.isRequired,
  children: PropTypes.node,
  overlayName: PropTypes.string.isRequired,
  obsDimensions: PropTypes.oneOfType([
    PropTypes.shape({
      width: PropTypes.string,
      height: PropTypes.string,
    }),
    PropTypes.arrayOf(
      PropTypes.shape({
        width: PropTypes.string,
        height: PropTypes.string,
      })
    ),
  ]),
  orientation: PropTypes.string,
  childrenSlot: PropTypes.oneOf([
    'stepOne',
    'stepTwo',
    'stepThree',
    'stepFour',
  ]),
  childrenSlotPosition: PropTypes.oneOf(['before', 'after']),
  finalStepSlotPosition: PropTypes.oneOf(['before', 'after', 'middle']),
  tiltifyCampaignSelected: PropTypes.func,
  pollSelected: PropTypes.func,
  finalStepContent: PropTypes.node,
  campaignStepContent: PropTypes.node,
  pollsFetched: PropTypes.func,
  constrainWidth: PropTypes.bool,
  showCopyDemoModeLinkButton: PropTypes.bool,
  showDemoModeLinkTip: PropTypes.bool,
};

const redirectURI =
  import.meta.env.VITE_TWITCH_CHATBOT_REDIRECT_URI ||
  `https://${window.location.hostname}/chatbot-landing`;

const twitchLambdaURI = `${import.meta.env.VITE_TWITCH_URI}`;

export default function LinkSetup({
  charityType,
  overlayURL,
  children,
  overlayName,
  obsDimensions,
  orientation,
  showTiltifyPollSelector = false,
  showTiltifyCampaignSelector = true,
  constrainWidth = true,
  childrenSlot = 'stepOne',
  childrenSlotPosition = 'before',
  finalStepSlotPosition = 'before',
  showCopyDemoModeLinkButton = false,
  showDemoModeLinkTip = true,
  tiltifyCampaignSelected = ({ slug, id, goal, name }) => {
    return { slug, id, goal, name };
  },
  pollSelected = ({ id, goal }) => {
    return { id, goal };
  },
  finalStepContent = null,
  campaignStepContent = null,
  pollsFetched = (polls) => {
    return polls;
  },
}) {
  let childrenStepOneBlockBefore,
    childrenStepOneBlockAfter,
    childrenStepTwoBlockBefore,
    childrenStepTwoBlockAfter,
    childrenStepThreeBlockBefore,
    childrenStepThreeBlockAfter,
    childrenStepFourBlockBefore,
    childrenStepFourBlockAfter,
    finalStepBlockBefore,
    finalStepBlockMiddle,
    finalStepBlockAfter;

  const queryClient = useQueryClient();
  const [showCampaignRetired, setShowCampaignRetired] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [copied, setCopied] = useState(false);
  const [copiedDemo, setCopiedDemo] = useState(false);
  const [twitchSignedIn, setTwitchSignedIn] = useState(false);
  const charityName = charityIsTiltify(charityType) ? 'Tiltify' : 'Twitch';

  const twitchConfig = useTwitchConfig();
  const tiltifyConfig = useTiltifyConfig();

  const charityRef = useRef(charityType);

  const overlayDemoModeURL = useMemo(() => {
    const url = new URL(overlayURL);
    url.searchParams.set('demoMode', 'yes');

    return url.toString();
  }, [overlayURL]);

  useEffect(() => {
    if (charityRef.current !== charityType) {
      setCurrentStep(1);
      charityRef.current = charityType
    }
    if (charityType === CHARITY.TILTIFY) setTwitchSignedIn(false);
  }, [charityType]);

  useEffect(() => {
    let timeout;

    if (showCampaignRetired) {
      timeout = setTimeout(() => {
        setShowCampaignRetired(false)
      }, 5000)
    }

    return () => {
      if (timeout)
        clearTimeout(timeout)
    }
  }, [showCampaignRetired]);

  const didCopy = useCallback((demoMode = false) => {
    if (demoMode) {
      setCopied(false);
      setCopiedDemo(true);
    } else {
      setCopied(true);
      setCopiedDemo(false);
      setCurrentStep(5);
    }

    const timeout = setTimeout(() => {
      if (demoMode) {
        setCopiedDemo(false);
      } else {
        setCopied(false);
      }
    }, 1800);

    toast(
      <>
        <CheckCircle />
        <span>Copied to clipboard</span>
      </>,
      {
        position: 'top-center',
        className: 'alert-success text-white',
      }
    );

    return () => {
      clearTimeout(timeout);
    }
  }, []);

  const { data: tiltifyPolls, isPending: tiltifyPollsLoading } = useQuery({
    queryKey: [
      'tiltify',
      tiltifyConfig.tiltifyUserID,
      'getPolls',
      tiltifyConfig.tiltifyCampaignID,
    ],
    queryFn: async () => {
      if (isTestMode) return [fakeTiltifyPoll];

      if (!tiltifyConfig.tiltifyCampaignID || !tiltifyConfig.tiltifyUserID)
        return [];
      tiltify.UseProxy();
      try {
        return await tiltify
          .GetPolls(tiltifyConfig.tiltifyCampaignID)
          .then((polls) => {
            pollsFetched(polls);
            return polls;
          });
      } catch (err) {
        throw new Error(err.message);
      }
    },
    placeholderData: [],
  });

  if (charityIsTiltify(charityType)) {
    if (
      tiltifyConfig.tiltifyUserID === null ||
      tiltifyConfig.tiltifyUserID.length === 0 ||
      tiltifyConfig.tiltifyUserSlug === null ||
      tiltifyConfig.tiltifyUserSlug.length === 0
    )
      setCurrentStep(1);
    else if (
      tiltifyConfig.tiltifyCampaignID === null ||
      tiltifyConfig.tiltifyCampaignID.length === 0
    )
      setCurrentStep(showTiltifyCampaignSelector ? 2 : 3);
    else if (
      showTiltifyPollSelector &&
      tiltifyConfig.tiltifyPollID !== null &&
      tiltifyConfig.tiltifyPollID.length > 0
    )
      setCurrentStep(4);
    else if (currentStep < 3) setCurrentStep(3);
  } else if (
    twitchConfig.twitchAccessToken === null ||
    twitchConfig.twitchAccessToken.length === 0
  ) {
    setCurrentStep(1);
  }

  if (finalStepContent) {
    if (finalStepSlotPosition === 'before')
      finalStepBlockBefore = finalStepContent;
    else if (finalStepSlotPosition === 'middle')
      finalStepBlockMiddle = finalStepContent;
    else finalStepBlockAfter = finalStepContent;
  }

  // Children slots
  if (children)
    if (childrenSlot === 'stepOne') {
      if (childrenSlotPosition === 'before')
        childrenStepOneBlockBefore = children;
      else childrenStepOneBlockAfter = children;
    } else if (childrenSlot === 'stepTwo') {
      if (childrenSlotPosition === 'before')
        childrenStepTwoBlockBefore = children;
      else childrenStepTwoBlockAfter = children;
    } else if (childrenSlot === 'stepThree') {
      if (childrenSlotPosition === 'before')
        childrenStepThreeBlockBefore = children;
      else childrenStepThreeBlockAfter = children;
    } else if (childrenSlot === 'stepFour') {
      if (childrenSlotPosition === 'before')
        childrenStepFourBlockBefore = children;
      else childrenStepFourBlockAfter = children;
    }

  /**
   * Handles the event when a campaign is selected by the user.
   * Updates the campaign details and manages the state of the current step.
   * Also, invalidates and re-fetches campaign related queries if poll selector is displayed.
   *
   * @param {Object} campaign - The campaign details.
   * @param {string} campaign.slug - The unique slug of the campaign.
   * @param {number} campaign.id - The unique identifier of the campaign.
   * @param {number} campaign.goal - The fundraising goal of the campaign.
   * @param {string} campaign.name - The name of the campaign.
   * @return {void}
   */
  function onTiltifyCampaignSelected({ slug, id, goal, name }) {
    tiltifyCampaignSelected({ slug, id, goal, name });
    const setStep = () => {
      if (currentStep !== 3 && slug && id && goal && name) setCurrentStep(3);
    };

    if (showTiltifyPollSelector)
      queryClient
        .invalidateQueries({
          queryKey: [
            'tiltify',
            tiltifyConfig.tiltifyUserID,
            'getPolls',
            tiltifyConfig.tiltifyCampaignID,
          ],
        })
        .then(() => setStep());
    else setStep();
  }

  function onTwitchCampaignSelected(twitchCampaign) {
    if (!twitchCampaign)
      setCurrentStep(2);
    else {
      const { id, goal, name } = twitchCampaign;
      if (currentStep !== 3 && id && goal && name) setCurrentStep(3);
    }
  }

  /**
   * Handles the event when a poll is selected.
   *
   * @param {Object} poll - The selected poll object.
   * @param {string} poll.id - The identifier of the selected poll.
   * @param {string} poll.goal - The goal of the selected poll.
   * @return {void}
   */
  function onPollSelected({ id, goal }) {
    if (currentStep === 3 && id && goal) setCurrentStep(4);
    pollSelected({ id, goal });
  }

  /**
   * Handles post sign-in actions for the Tiltify platform.
   *
   * @param {Object} userInfo - An object containing user identification information.
   * @param {string} userInfo.userID - The unique identifier for the signed-in user.
   * @param {string} userInfo.username - The username of the signed-in user.
   * @param {string} userInfo.slug - The user-specific slug used within the Tiltify platform.
   * @return {void}
   */
  function tiltifyDidSignIn({ userID, username, slug }) {
    setTwitchSignedIn(false);
    if (currentStep === 1 && userID && username && slug) setCurrentStep(2);
  }

  function tiltifyDidSignOut() {
    if (currentStep !== 1) setCurrentStep(1);
  }

  function twitchDidSignIn({ username, accessToken, refreshToken }) {
    if (accessToken && refreshToken && username) setTwitchSignedIn(true);

    if (currentStep === 1 && accessToken && refreshToken && username)
      setCurrentStep(2);
  }

  function twitchDidSignOut() {
    twitchConfig.twitchAccessToken = '';
    twitchConfig.twitchCampaignID = '';
    twitchConfig.twitchUsername = '';
    twitchConfig.twitchBroadcasterID = '';
    setTwitchSignedIn(false);
    setCurrentStep(1);
  }

  function tiltifyCampaignRetired() {
    tiltifyConfig.tiltifyCampaignID = "";
    tiltifyConfig.tiltifyCampaignSlug = "";
    tiltifyConfig.tiltifyCampaignGoal = "0.00";
    tiltifyConfig.tiltifyCampaignName = "";

    localStorage.removeItem('tiltifyCampaignID');
    localStorage.removeItem('tiltifyCampaignSlug');
    localStorage.removeItem('tiltifyCampaignGoal');
    localStorage.removeItem('tiltifyCampaignName');

    const userID = tiltifyConfig.tiltifyUserID;
    const username = tiltifyConfig.tiltifyUsername;
    const slug = tiltifyConfig.tiltifyUserSlug;

    setShowCampaignRetired(true);
    if (currentStep >=2 && userID && username && slug) setCurrentStep(2);
  }

  function getStepOne() {
    if (charityIsTiltify(charityType))
      return (
        <TiltifySetup
          onSignIn={tiltifyDidSignIn}
          onSignOut={tiltifyDidSignOut}
        />
      );
    else
      return (
        <TwitchSetup
          lambdaURI={twitchLambdaURI}
          redirectURI={redirectURI}
          onSignOut={twitchDidSignOut}
          onSignIn={twitchDidSignIn}
        />
      );
  }

  useEffect(() => {
    if (copied) setTimeout(() => setCopied(false), 5000);
  }, [copied]);

  return (
    <div
      className={`min-h-link-setup ${constrainWidth ? 'max-w-[1500px]' : 'w-full xl:mt-4'}`}
    >
      <LinkSetupStep
        step={1}
        currentStep={currentStep}
        title={`Connect the ${overlayName} to ${charityName}`}
      >
        {childrenStepOneBlockBefore}
        {getStepOne()}
        {childrenStepOneBlockAfter}
      </LinkSetupStep>

      {showTiltifyCampaignSelector && (
        <LinkSetupStep
          step={2}
          currentStep={currentStep}
          title={
            charityIsTiltify(charityType)
              ? 'Select your Fundraising Campaign'
              : 'Confirm your Fundraising Campaign'
          }
        >
          {childrenStepTwoBlockBefore}
          {charityIsTiltify(charityType) && (
            <TiltifyCampaignSelector
              allowRetired={false}
              showCampaignRetired={showCampaignRetired}
              onCampaignRetired={tiltifyCampaignRetired}
              onCampaignSelected={onTiltifyCampaignSelected}
              onCampaignRemoved={tiltifyCampaignRetired}
            />
          )}
          {charityIsTwitch(charityType) && (
            <TwitchCampaignViewer
              disabled={!twitchSignedIn}
              onCampaignSelected={onTwitchCampaignSelected}
            />
          )}
          {campaignStepContent}
          {childrenStepTwoBlockAfter}
        </LinkSetupStep>
      )}

      {showTiltifyPollSelector && (
        <LinkSetupStep
          step={3}
          currentStep={currentStep}
          title={'Select your Poll'}
        >
          {childrenStepThreeBlockBefore}
          <TiltifyPollSelector
            onPollSelected={onPollSelected}
            polls={tiltifyPolls}
            pollsLoading={tiltifyPollsLoading}
          />
          {childrenStepThreeBlockAfter}
        </LinkSetupStep>
      )}

      <LinkSetupStep
        step={showTiltifyPollSelector ? 4 : showTiltifyCampaignSelector ? 3 : 2}
        currentStep={currentStep}
        title={`Add the ${overlayName} to OBS`}
      >
        {showTiltifyPollSelector
          ? childrenStepFourBlockBefore
          : childrenStepThreeBlockBefore}
        {finalStepBlockBefore}
        <article className="prose text-xs mb-2 mt-2">
          Copy and paste the overlay link (below) into your streaming software
        </article>
        <input
          type="text"
          value={overlayURL}
          readOnly
          className="input input-bordered mb-4 w-full "
        />

        {showCopyDemoModeLinkButton && showDemoModeLinkTip && (
          <div role="alert" className="alert alert-info bg-blue-400 text-white border-blue-400 mb-2">
            <InfoCircle/>
            <span className="text-sm">Tip: Use the demo link to visualize your overlay before getting donations.</span>
          </div>
        )}

        <div className="flex justify-between w-full">
          <CopyToClipboard text={overlayURL} onCopy={() => didCopy(false)}>
            <button
              data-track="copy-link"
              className={`btn ${copied ? 'btn-neutral' : 'btn-secondary'} basis-[48%] flex-grow flex items-center justify-center`}
            >
              <span>{copied ? 'Copied' : 'Copy Link to Clipboard'}</span>
            </button>
          </CopyToClipboard>

          {showCopyDemoModeLinkButton && (
           <>
             <span className="flex-grow"/>
             <CopyToClipboard text={overlayDemoModeURL} onCopy={() => didCopy(true)}>
               <button
                 data-track="copy-demo-link"
                 className={`btn ${copiedDemo ? 'btn-neutral' : 'btn-accent'} basis-[48%] flex-grow flex items-center justify-center`}
               >
                 <span>{copiedDemo ? 'Copied Demo Link' : 'Copy Demo Link to Clipboard'}</span>
               </button>
             </CopyToClipboard>
           </>
          )}
        </div>

        {finalStepBlockMiddle}

        <div className="mt-10">
          <p>
            <strong>Add overlay to OBS:</strong>
          </p>

          <ol>
            <li>
              1. Add a new browser source and give it a name like &apos;St. Jude{' '}
              {overlayName}&apos;
            </li>
            <li>2. Copy and paste url into URL field</li>
            <OBSDimensions
              obsDimensions={obsDimensions}
              orientation={orientation}
            />
            <li>4. Click OK</li>
          </ol>
        </div>
        {showTiltifyPollSelector
          ? childrenStepFourBlockAfter
          : childrenStepThreeBlockAfter}
        {finalStepBlockAfter}
      </LinkSetupStep>
    </div>
  );
}
